import { ReactElement, MouseEvent, useCallback } from 'react';
import {
  Box,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { useIntl } from 'react-intl';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import { ImageDisplayStyle } from 'shared/domain/report/types/model/common';
import {
  formatTranslation,
  imageDisplayStyleLabelKey,
  imageDisplayStyleTooltipLabelKey,
} from 'intl';
import { IMAGE_DISPLAY_STYLE_UI_ORDER } from '../../reportTooltipLabels';
import { ReportTranslationKeys } from 'intl/keys';
import {
  getReportSegmentedControlWrapperSx,
  REPORT_SEGMENTED_CONTROL_GROUP_SX,
  REPORT_SEGMENTED_CONTROL_LABEL_SX,
} from '../../reportSegmentedControlStyles';

const DISPLAY_STYLE_ICON_SIZE = 16;

const ICON_SX = { fontSize: DISPLAY_STYLE_ICON_SIZE, color: 'inherit' };

const DISPLAY_STYLE_ICONS: Record<ImageDisplayStyle, ReactElement> = {
  [ImageDisplayStyle.miniatures]: <GridViewOutlined sx={ICON_SX} />,
  [ImageDisplayStyle.list]: <FormatListBulleted sx={ICON_SX} />,
  [ImageDisplayStyle.fullPage]: <PictureAsPdf sx={ICON_SX} />,
};

export function DisplayStyleSegmentedControl({
  value,
  onChange,
  disabled,
  displayStyles = IMAGE_DISPLAY_STYLE_UI_ORDER,
  'data-qa': dataQa,
}: {
  value: ImageDisplayStyle;
  onChange: (displayStyle: ImageDisplayStyle) => void;
  disabled?: boolean;
  displayStyles?: ImageDisplayStyle[];
  'data-qa'?: string;
}): ReactElement {
  const intl = useIntl();

  const handleChange = useCallback(
    (_: MouseEvent<HTMLElement>, next: ImageDisplayStyle | null) => {
      if (next !== null && !disabled) {
        onChange(next);
      }
    },
    [disabled, onChange]
  );

  const labels = displayStyles.map((style) =>
    formatTranslation(intl, imageDisplayStyleLabelKey(style))
  );

  return (
    <Box
      aria-disabled={disabled || undefined}
      sx={getReportSegmentedControlWrapperSx(disabled)}
      data-qa={dataQa}
    >
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        size='small'
        aria-label={formatTranslation(intl, ReportTranslationKeys.suboptionDisplayStyle,)}
        sx={REPORT_SEGMENTED_CONTROL_GROUP_SX}
      >
        {displayStyles.map((style, index) => (
          <MuiToggleButton
            key={style}
            value={style}
            data-qa={dataQa ? `${dataQa}_${style}` : undefined}
          >
            <Tooltip
              title={formatTranslation(
                intl,
                imageDisplayStyleTooltipLabelKey(style)
              )}
              arrow
              placement='top'
            >
              <Box component='span' sx={REPORT_SEGMENTED_CONTROL_LABEL_SX}>
                {DISPLAY_STYLE_ICONS[style]}
                {labels[index]}
              </Box>
            </Tooltip>
          </MuiToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}
